import {
  CARD_PRESENT_POLL_INTERVAL,
  CARD_PRESENT_POLL_TIMEOUT,
} from "src/main/Billing/constants"

import {
  cancelPayment,
  createSale,
  updateSalePayment,
} from "src/api/PointOfSale/checkout"
import { fetchSale } from "src/api/PointOfSale/sales"

import { SALE_STATUS } from "../../constants"

const MAX_CONSECUTIVE_POLL_FAILURES = 5
const POLL_REQUEST_TIMEOUT = 10000 // 10 seconds
// Longer than the server's 15-second request timeout: aborting does not stop the server, so
// a cancel abandoned early can still succeed, and its retry is then refused with a 400.
const CANCEL_REQUEST_TIMEOUT = 20000 // 20 seconds

const processSale = async (
  { saleParams, amount, paymentMethod, saleId },
  cancelPaymentRef,
  showToast,
  enableBluetoothCardReader,
  setPaymentMethod
) => {
  const saleResponse = await beginSale({
    saleParams,
    amount,
    paymentMethod,
    saleId,
  })

  if (!saleResponse) {
    throw new Error("Something went wrong. Please try again")
  }

  // Until the reader confirms the payment, the modal keeps its "Connecting to" copy.
  const showProcessingCopy = () =>
    setPaymentMethod((pm) => ({
      ...pm,
      processingCopy: "Processing payment...",
    }))
  const readerUnconfirmed = Boolean(saleResponse.sale?.readerUnconfirmed)
  if (!readerUnconfirmed) {
    showProcessingCopy()
  }

  // If we're in an iOS webview and we get a clientSecret back, that means we selected the bluetooth card reader
  // In that case, we need to tell the iOS app to collect payment using the bluetooth card reader
  // To handle the bluetooth card reader interaction, the Stripe iOS SDK needs the PaymentIntent's client_secret
  if (enableBluetoothCardReader && saleResponse.clientSecret) {
    // Pass the client_secret to the iOS app so it can kick off the bluetooth card reader interaction
    // Note: the iOS webview is configured so that it detects but does not follow dockwa:// links
    // so setting the href here will not navigate/change the page, but does allow the iOS app to extract the clientSecret
    window.location.href = `dockwa://payment_intents/${saleResponse.clientSecret}`
  }

  return pollSale({
    saleResponse,
    marinaSlug: saleParams.manage_id,
    cancelPaymentRef,
    showToast,
    readerUnconfirmed,
    onReaderConfirmed: showProcessingCopy,
  })
}

// Never throws: the sale already exists, so throwing would send the cashier back to
// the confirmation screen, where confirming again would start a second sale. A payment
// whose outcome can't be read in time comes back as "processing" instead.
async function pollSale({
  saleResponse,
  marinaSlug,
  cancelPaymentRef,
  showToast,
  readerUnconfirmed,
  onReaderConfirmed,
}) {
  const saleId = saleResponse.sale.encodedId
  const deadline = Date.now() + CARD_PRESENT_POLL_TIMEOUT
  let latestResponse = saleResponse
  let consecutiveFailures = 0
  let awaitingReader = readerUnconfirmed
  const showCancelTooLate = () =>
    showToast(
      "Cannot cancel payment, customer has already presented their card. Please wait for payment response.",
      { type: "error" }
    )

  while (
    Date.now() < deadline &&
    consecutiveFailures < MAX_CONSECUTIVE_POLL_FAILURES
  ) {
    await new Promise((resolve) =>
      setTimeout(resolve, CARD_PRESENT_POLL_INTERVAL)
    )

    // A refused cancel can mean the payment already failed, not that the customer
    // presented a card, so the sale is read before the cashier is told to wait.
    let cancelRefused = false
    if (cancelPaymentRef.current) {
      try {
        await cancelPaymentWithTimeout({ marinaSlug, saleId })
        return withSaleStatus(latestResponse, SALE_STATUS.canceled)
      } catch (error) {
        if (error.status !== 400) {
          consecutiveFailures += 1
          continue
        }
        cancelPaymentRef.current = false
        cancelRefused = true
      }
    }

    try {
      latestResponse = await fetchSaleWithTimeout({ marinaSlug, saleId })
      consecutiveFailures = 0
    } catch {
      consecutiveFailures += 1
      if (cancelRefused) {
        showCancelTooLate()
      }
      continue
    }

    if (
      [SALE_STATUS.settled, SALE_STATUS.failed].includes(
        latestResponse.sale?.status
      )
    ) {
      return latestResponse
    }
    if (cancelRefused) {
      showCancelTooLate()
    }
    if (awaitingReader && !latestResponse.sale?.readerUnconfirmed) {
      awaitingReader = false
      onReaderConfirmed()
    }
  }

  return withSaleStatus(latestResponse, SALE_STATUS.processing)
}

function fetchSaleWithTimeout({ marinaSlug, saleId }) {
  return withRequestTimeout(
    (signal) => fetchSale({ marinaSlug, saleId, signal }),
    POLL_REQUEST_TIMEOUT
  )
}

function cancelPaymentWithTimeout({ marinaSlug, saleId }) {
  return withRequestTimeout(
    (signal) => cancelPayment({ marinaSlug, saleId, signal }),
    CANCEL_REQUEST_TIMEOUT
  )
}

async function withRequestTimeout(request, timeout) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)
  try {
    return await request(controller.signal)
  } finally {
    clearTimeout(timeoutId)
  }
}

function withSaleStatus(response, status) {
  return { ...response, sale: { ...response.sale, status } }
}

async function beginSale({ saleParams, amount, paymentMethod, saleId }) {
  const paymentTxnAttributes = {
    type: "Billing::StripePaymentTxn",
    amount,
    stripe_payment_attributes: {
      card_reader_id: paymentMethod.cardReader.id,
    },
  }

  try {
    return saleId
      ? await updateSalePayment(
          {
            id: saleId,
            marinaSlug: saleParams.manage_id,
          },
          {
            payment_txn_attributes: paymentTxnAttributes,
          }
        )
      : await createSale({
          ...saleParams,
          payment_txn_attributes: paymentTxnAttributes,
        })
  } catch (e) {
    if (e.status === 503) {
      throw new Error("Card reader unavailable. Please try again.")
    }

    throw e
  }
}

export default processSale
